import React, { useState, useEffect } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Task } from "../types";
import { TaskStatusKey, taskStatusColors } from "../utils/statusColors";
import { fromServerToLocal } from "../utils/time";
import StatusChangeModal, { StatusType } from "./Modals/StatusChangeModal";
import { updateTask } from "../services/taskService";
import { getCachedStatuses } from "../storage/preloadData";

interface TaskViewProps {
  task: Task;
  onStatusChange?: () => void;
  onStatusUpdate?: (newStatus: string, resolution?: string) => Promise<void>;
  onStatusUpdateSuccess?: (message: string) => void;
  statusTypeOverride?: StatusType;
  availableStatusesOverride?: string[];
  statusColorsOverride?: Record<string, string>;
  currentStatusOverride?: string;
}

const TaskView: React.FC<TaskViewProps> = ({
  task,
  onStatusChange,
  onStatusUpdate,
  onStatusUpdateSuccess,
  statusTypeOverride,
  availableStatusesOverride,
  statusColorsOverride,
  currentStatusOverride,
}) => {
  const { t, i18n } = useTranslation();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (availableStatusesOverride !== undefined) {
      setAvailableStatuses(availableStatusesOverride);
      return;
    }

    const loadStatuses = async () => {
      try {
        const statuses = await getCachedStatuses();
        const taskStatuses = statuses.tasks || [];
        if (taskStatuses.length > 0) {
          setAvailableStatuses(taskStatuses);
        } else {
          setAvailableStatuses([
            "Not Started",
            "Started",
            "Completed",
            "Deferred",
            "Сanceled",
          ]);
        }
      } catch (err) {
        console.error("Failed to load task statuses:", err);
        setAvailableStatuses([
          "Not Started",
          "Started",
          "Completed",
          "Deferred",
          "Сanceled",
        ]);
      }
    };
    loadStatuses();
  }, [availableStatusesOverride]);

  const handleStatusSave = async (newStatus: string, resolution?: string) => {
    setIsLoading(true);
    try {
      if (onStatusUpdate) {
        await onStatusUpdate(newStatus, resolution);
      } else {
        const updateData: Partial<Task> = { status: newStatus };
        if (newStatus === "Completed") {
          updateData.cSolution = resolution || "";
        }
        await updateTask(task.id, updateData);
      }
      if (onStatusUpdateSuccess) {
        onStatusUpdateSuccess(
          t("statusChangeModal.success") || "Статус успешно изменен",
        );
      }
      if (onStatusChange) {
        await onStatusChange();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const statusColorsMap: Record<string, string> =
    statusColorsOverride ||
    Object.keys(taskStatusColors).reduce(
      (acc, key) => {
        acc[key] = taskStatusColors[key as TaskStatusKey].color;
        return acc;
      },
      {} as Record<string, string>,
    );

  const dateFormat =
    i18n.language === "en_US" ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY HH:mm";

  const startLocal = task.dateStart ? fromServerToLocal(task.dateStart) : null;
  const endLocal = task.dateEnd ? fromServerToLocal(task.dateEnd) : null;
  const completedLocal = task.dateCompleted
    ? fromServerToLocal(task.dateCompleted)
    : null;

  const formattedStart =
    startLocal && dayjs(startLocal).isValid()
      ? dayjs(startLocal).format(dateFormat)
      : null;

  const formattedEnd =
    endLocal && dayjs(endLocal).isValid()
      ? dayjs(endLocal).format(dateFormat)
      : null;

  const formattedCompleted =
    completedLocal && dayjs(completedLocal).isValid()
      ? dayjs(completedLocal).format(dateFormat)
      : null;

  const effectiveStatus = currentStatusOverride ?? task.status ?? "";
  const normalizedStatus = effectiveStatus.toLowerCase().replace(/\s/g, "_");
  const statusText =
    statusTypeOverride === "projectTask"
      ? t(`statuses.projectTask.${normalizedStatus}`, effectiveStatus)
      : t(`statuses.task.${normalizedStatus}`, effectiveStatus);
  const statusColor =
    statusColorsMap[effectiveStatus] ||
    taskStatusColors[task.status as TaskStatusKey]?.color ||
    "#757575";

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" gap={2.5}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              mb: 0.5,
              display: "block",
            }}
          >
            {t("tasks.detail.nameLabel") || "Название"}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "1rem", fontWeight: 500 }}
          >
            {task.name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 3, sm: 4 },
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("tasks.detail.statusLabel") || "Статус"}
            </Typography>
            <Chip
              label={statusText}
              onClick={() => setStatusModalOpen(true)}
              sx={{
                backgroundColor: statusColor,
                color: "#fff",
                borderRadius: "16px",
                py: 0.5,
                fontSize: "0.875rem",
                fontWeight: 600,
                height: "auto",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            />
          </Box>

          {task.assignedUserName && (
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mb: 0.5,
                  display: "block",
                }}
              >
                {t("tasks.detail.assigneeLabel") || "Исполнитель"}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                {task.assignedUserName}
              </Typography>
            </Box>
          )}
        </Box>

        {(formattedStart || formattedEnd) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 2, sm: 4 },
              alignItems: "flex-start",
            }}
          >
            {formattedStart && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("tasks.detail.startDateLabel") || "Дата начала"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {formattedStart}
                </Typography>
              </Box>
            )}
            {formattedEnd && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("tasks.detail.endDateLabel") || "Дата окончания"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {formattedEnd}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {task.description && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("tasks.detail.descriptionLabel") || "Описание"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {task.description}
            </Typography>
          </Box>
        )}

        {task.status === "Completed" && (task.cSolution || task.resolution) && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("tasks.detail.resolutionLabel") || "Результат"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {task.cSolution || task.resolution}
            </Typography>
          </Box>
        )}

        {formattedCompleted && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("tasks.detail.completedDateLabel") || "Дата завершения"}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1rem" }}>
              {formattedCompleted}
            </Typography>
          </Box>
        )}
      </Box>
      <StatusChangeModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSave={handleStatusSave}
        currentStatus={effectiveStatus}
        availableStatuses={availableStatuses}
        statusType={statusTypeOverride || "task"}
        statusColors={statusColorsMap}
        isLoading={isLoading}
        currentResolution={task.cSolution || task.resolution}
      />
    </Box>
  );
};

export default TaskView;
